import { useGridDefinitions } from "@/controls/ag-grid/components/GridDefinitions";
import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { ConfirmationDialogRaw } from "@/controls/modals/ConfirmationDialogRaw";
import { TextFieldSearchFilter } from "@/shared/components/TextFieldSearchFilter";
import {
	ErrorModal,
	useContextMenuMESF,
	useHasPermission,
} from "@dexteel/mesf-core";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PublishIcon from "@mui/icons-material/Publish";
import UndoIcon from "@mui/icons-material/Undo";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import {
	Box,
	Button,
	Chip,
	FormControl,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Tooltip,
	Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
	CellValueChangedEvent,
	DefaultMenuItem,
	GetContextMenuItemsParams,
	GridApi,
	MenuItemDef,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useState } from "react";
import {
	TestsBySection,
	TestsBySectionVersion,
} from "../../../models/TestsBySection";
import {
	confirmTestsBySectionVersion,
	createTestsBySectionVersion,
	deleteTestsBySection,
	deleteTestsBySectionVersion,
	getTestsBySection,
	getTestsBySectionVersions,
	unscheduleTestsBySectionVersion,
	upsertTestsBySection,
} from "../../../repositories/TestsBySectionRepository";
import { useTableData } from "../hooks/useTableData";
import { useTestsBySectionOptionsFunctions } from "../hooks/useTestsBySectionOptionsFunctions";
import { AddRowModal, RowPayload } from "./AddRowModal";
import { CheckboxCellContext } from "./CheckboxCell";
import {
	StatusIcon,
	VERSION_STATUS,
} from "@/pages/configuration/versions/versionStatus";
import { NewVersionModal } from "@/pages/configuration/versions/NewVersionModal";
import { ConfirmVersionModal } from "@/pages/configuration/versions/ConfirmVersionModal";

const versionLabel = (version: TestsBySectionVersion) =>
	version.StatusCode === VERSION_STATUS.DRAFT
		? "Draft"
		: `Rev ${version.Rev} - ${new Date(
				version.EffectiveDate ?? new Date(),
			).toLocaleDateString()}`;

const currentVersionId = (versions: TestsBySectionVersion[]) =>
	versions
		.filter((v) => v.StatusCode === VERSION_STATUS.PUBLISHED)
		.sort(
			(a, b) =>
				new Date(b.EffectiveDate ?? new Date()).getTime() -
				new Date(a.EffectiveDate ?? new Date()).getTime(),
		)[0]?.TestsBySectionVersionId ?? null;

const VersionOption = ({
	version,
	isCurrent,
}: {
	version: TestsBySectionVersion;
	isCurrent: boolean;
}) => (
	<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
		<StatusIcon
			status={version.StatusCode}
			isCurrent={isCurrent}
			date={
				version.EffectiveDate == null
					? undefined
					: new Date(version.EffectiveDate).toLocaleDateString()
			}
		/>
		{versionLabel(version)}
	</Box>
);

export const applyToggle = (
	row: TestsBySection,
	field: keyof TestsBySection,
): TestsBySection => {
	const next: TestsBySection = { ...row, [field]: !row[field] };

	const isPull = field === "Pull2" || field === "Pull8" || field === "Pull505";
	if (isPull && !row[field]) {
		next.Pull2 = field === "Pull2";
		next.Pull8 = field === "Pull8";
		next.Pull505 = field === "Pull505";
	}

	return next;
};

export const useTestsBySectionVersions = () =>
	useQuery<TestsBySectionVersion[], Error>({
		queryKey: ["testsBySectionVersions"],
		queryFn: ({ signal }) => getTestsBySectionVersions(signal),
	});

export const useTestsBySection = (versionId: number | null) =>
	useQuery<TestsBySection[], Error>({
		queryKey: ["testsBySection", versionId],
		queryFn: ({ signal }) => getTestsBySection({ versionId }, signal),
		enabled: versionId != null,
	});

type Props = {
	readOnly?: boolean;
};

export const TestsBySectionTable = ({ readOnly = false }: Props) => {
	const queryClient = useQueryClient();
	const canEditPublished = useHasPermission()(
		"ui.configuration.tests.by.section.edit-published",
	);

	const [filterValue, setFilterValue] = useState("");
	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const [error, setError] = useState("");
	const [selectedVersionId, setSelectedVersionId] = useState<number | null>(
		null,
	);
	const [addRowOpen, setAddRowOpen] = useState(false);
	const [newVersionOpen, setNewVersionOpen] = useState(false);
	const [confirmVersionOpen, setConfirmVersionOpen] = useState(false);
	const [editRow, setEditRow] = useState<TestsBySection | null>(null);
	const [rowToDelete, setRowToDelete] = useState<TestsBySection | null>(null);
	const [confirm, setConfirm] = useState<"changeToDraft" | "delete" | null>(
		null,
	);
	const [isEditingPublished, setIsEditingPublished] = useState(false);

	const {
		data: allVersions,
		isLoading: versionsLoading,
		error: versionsError,
		isError: isVersionsError,
	} = useTestsBySectionVersions();

	const versions = readOnly
		? allVersions?.filter((v) => v.StatusCode !== VERSION_STATUS.DRAFT)
		: allVersions;
	const orderedVersions = [...(versions ?? [])].sort(
		(a, b) =>
			Number(b.StatusCode === VERSION_STATUS.DRAFT) -
			Number(a.StatusCode === VERSION_STATUS.DRAFT),
	);

	const defaultVersionId =
		versions?.find((v) => v.StatusCode === VERSION_STATUS.PUBLISHED)
			?.TestsBySectionVersionId ??
		versions?.[0]?.TestsBySectionVersionId ??
		null;

	const versionId = selectedVersionId ?? defaultVersionId;
	const currentId = currentVersionId(versions ?? []);

	const {
		data: tests,
		isLoading: testsLoading,
		error: testsError,
		isError: isTestsError,
	} = useTestsBySection(versionId);

	const selectedVersion =
		versions?.find((v) => v.TestsBySectionVersionId === versionId) ?? null;
	const isDraft =
		!readOnly && selectedVersion?.StatusCode === VERSION_STATUS.DRAFT;
	const isScheduled =
		!readOnly && selectedVersion?.StatusCode === VERSION_STATUS.SCHEDULED;
	const isLatestPublished =
		!readOnly &&
		selectedVersion?.StatusCode === VERSION_STATUS.PUBLISHED &&
		selectedVersion.IsLatest;
	const isCorrecting =
		isEditingPublished && isLatestPublished && canEditPublished;
	const isEditMode = isDraft || isCorrecting;
	const hasTestRuns = (selectedVersion?.TestRunCount ?? 0) > 0;
	const draftVersionId =
		(allVersions ?? []).find((v) => v.StatusCode === VERSION_STATUS.DRAFT)
			?.TestsBySectionVersionId ?? null;
	const hasDraft = draftVersionId != null;
	const hasPendingVersion = (allVersions ?? []).some(
		(v) => v.StatusCode !== VERSION_STATUS.PUBLISHED,
	);
	const confirmedVersions = (allVersions ?? []).filter((v) => v.IsConfirmed);
	const maxRev = confirmedVersions.reduce(
		(max, v) => Math.max(max, v.Rev ?? 0),
		0,
	);
	const lastEffectiveDate = confirmedVersions.reduce<Date | null>(
		(last, v) =>
			v.EffectiveDate != null &&
			(last == null || new Date(v.EffectiveDate) > new Date(last))
				? v.EffectiveDate
				: last,
		null,
	);

	const upsertMutation = useMutation({
		mutationFn: upsertTestsBySection,
		meta: { successMessage: "The test was saved successfully" },
		onSuccess: async () =>
			await queryClient.invalidateQueries({
				queryKey: ["testsBySection", versionId],
			}),
		onError: (e: Error) => setError(e.message),
	});

	const deleteMutation = useMutation({
		mutationFn: deleteTestsBySection,
		meta: { successMessage: "The test was deleted successfully" },
		onSuccess: async () =>
			await queryClient.invalidateQueries({
				queryKey: ["testsBySection", versionId],
			}),
		onError: (e: Error) => setError(e.message),
	});

	const createVersionMutation = useMutation({
		mutationFn: createTestsBySectionVersion,
		meta: {
			successMessage:
				"Draft created — edit the tests, then Confirm when ready.",
		},
		onSuccess: async (newId: number) => {
			await queryClient.invalidateQueries({
				queryKey: ["testsBySectionVersions"],
			});
			setSelectedVersionId(newId);
			setNewVersionOpen(false);
		},
		onError: (e: Error) => setError(e.message),
	});

	const confirmMutation = useMutation({
		mutationFn: confirmTestsBySectionVersion,
		meta: { successMessage: "Specification confirmed" },
		onSuccess: async () => {
			await queryClient.invalidateQueries({
				queryKey: ["testsBySectionVersions"],
			});
			setConfirmVersionOpen(false);
		},
		onError: (e: Error) => setError(e.message),
	});

	const unscheduleMutation = useMutation({
		mutationFn: unscheduleTestsBySectionVersion,
		meta: { successMessage: "Revision changed to Draft" },
		onSuccess: async () => {
			await queryClient.invalidateQueries({
				queryKey: ["testsBySectionVersions"],
			});
		},
		onError: (e: Error) => setError(e.message),
	});

	const discardMutation = useMutation({
		mutationFn: deleteTestsBySectionVersion,
		meta: { successMessage: "Draft deleted" },
		onSuccess: async () => {
			setSelectedVersionId(null);
			await queryClient.invalidateQueries({
				queryKey: ["testsBySectionVersions"],
			});
		},
		onError: async (e: Error) => {
			setError(e.message);
			await queryClient.invalidateQueries({
				queryKey: ["testsBySectionVersions"],
			});
		},
	});

	const isSubmitting = upsertMutation.isPending || deleteMutation.isPending;

	const saveRow = (row: TestsBySection) => {
		if (versionId == null) return;
		upsertMutation.mutate({ testsBySection: row, versionId });
	};

	const handleSaveRow = (payload: RowPayload) => {
		if (versionId == null) return;
		upsertMutation.mutate(
			{ testsBySection: payload as TestsBySection, versionId },
			{
				onSuccess: () => {
					setAddRowOpen(false);
					setEditRow(null);
				},
			},
		);
	};

	const handleEditRow = (row: TestsBySection) => {
		setEditRow(row);
		setAddRowOpen(true);
	};

	const handleDelete = (row: TestsBySection) => {
		if (row.TestsBySectionId == null) return;
		setRowToDelete(row);
	};

	const handleDeleteRowClose = (value?: string) => {
		const row = rowToDelete;
		setRowToDelete(null);
		if (value !== "OK" || row?.TestsBySectionId == null) return;
		deleteMutation.mutate({ testsBySectionId: row.TestsBySectionId });
	};

	const handleConfirmClose = (value?: string) => {
		const action = confirm;
		setConfirm(null);
		if (value !== "OK" || versionId == null) return;
		if (action === "changeToDraft") unscheduleMutation.mutate({ versionId });
		if (action === "delete") discardMutation.mutate({ versionId });
	};

	const onCellValueChanged = (event: CellValueChangedEvent<TestsBySection>) => {
		if (event.colDef.field !== "Comments") return;
		if (event.oldValue === event.newValue) return;
		saveRow(event.data);
	};

	const { columnDefs } = useTableData({ isEditMode });

	const backToDraftButton = (
		<Tooltip
			title={
				hasDraft
					? "There is already a draft. Edit or delete it first."
					: hasTestRuns
						? "Mechanical test results were received under this revision, so it stays as history"
						: ""
			}
		>
			<span>
				<Button
					variant="outlined"
					size="small"
					startIcon={<UndoIcon />}
					onClick={() => setConfirm("changeToDraft")}
					disabled={unscheduleMutation.isPending || hasDraft || hasTestRuns}
					data-test-id="change-to-draft-button"
				>
					Back to Draft
				</Button>
			</span>
		</Tooltip>
	);
	const { defaultColDef } = useGridDefinitions({});

	const { showContextMenu, registerConfig } = useContextMenuMESF();
	const { getMenuOptions } = useTestsBySectionOptionsFunctions({
		onEdit: handleEditRow,
		onDelete: handleDelete,
	});

	const getContextMenuItems = useCallback(
		(params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
			const data = params.node?.data;
			if (isEditMode && data) {
				showContextMenu(event as any, data, "testsBySectionContext");
			}
			return [];
		},
		[isEditMode],
	);

	useEffect(() => {
		registerConfig({
			id: "testsBySectionContext",
			getOptions: getMenuOptions,
		});
	}, []);

	useEffect(() => {
		if (isVersionsError) {
			setError(versionsError.message);
		}
		if (isTestsError) {
			setError(testsError.message);
		}
	}, [versionsError, isVersionsError, testsError, isTestsError]);

	return (
		<>
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					height: readOnly ? "78vh" : "85vh",
					p: 1,
					gap: 2,
				}}
			>
				{!readOnly && (
					<Typography variant="h5" fontWeight={600}>
						Tests by Section
					</Typography>
				)}

				<Box
					sx={{
						display: "flex",
						gap: 2,
						alignItems: "center",
						justifyContent: "space-between",
						flexWrap: "wrap",
					}}
				>
					<Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
						<FormControl size="small" sx={{ minWidth: 240 }}>
							<InputLabel id="tbs-version-label">Version</InputLabel>
							<Select
								labelId="tbs-version-label"
								label="Version"
								data-test-id="version-selector"
								value={versionId ?? ""}
								disabled={versionsLoading || isCorrecting}
								onChange={(e) => setSelectedVersionId(Number(e.target.value))}
								renderValue={(value) => {
									const version = versions?.find(
										(v) => v.TestsBySectionVersionId === value,
									);
									return version ? (
										<VersionOption
											version={version}
											isCurrent={version.TestsBySectionVersionId === currentId}
										/>
									) : null;
								}}
								sx={{
									backgroundColor: (theme) => theme.palette.background.paper,
								}}
							>
								{orderedVersions.map((v) => (
									<MenuItem
										key={v.TestsBySectionVersionId}
										value={v.TestsBySectionVersionId}
									>
										<VersionOption
											version={v}
											isCurrent={v.TestsBySectionVersionId === currentId}
										/>
									</MenuItem>
								))}
							</Select>
						</FormControl>

						{isDraft && (
							<Chip
								label="Draft"
								color="warning"
								size="small"
								data-test-id="draft-chip"
							/>
						)}
						{isScheduled && (
							<Chip
								label="Scheduled"
								color="info"
								size="small"
								data-test-id="scheduled-chip"
							/>
						)}
						{isCorrecting && (
							<Chip
								label="Editing published revision"
								color="warning"
								size="small"
								data-test-id="editing-published-chip"
							/>
						)}
					</Box>

					<Box sx={{ display: "flex", gap: 1 }}>
						{readOnly ? null : isCorrecting ? (
							<>
								<Button
									variant="outlined"
									size="small"
									startIcon={<AddIcon />}
									data-test-id="add-row-button"
									onClick={() => {
										setEditRow(null);
										setAddRowOpen(true);
									}}
									sx={{
										backgroundColor: (theme) => theme.palette.background.paper,
									}}
								>
									Add Row
								</Button>
								<Button
									variant="contained"
									size="small"
									startIcon={<CheckIcon />}
									data-test-id="done-editing-published-button"
									onClick={() => setIsEditingPublished(false)}
									disabled={isSubmitting}
								>
									Done
								</Button>
							</>
						) : isEditMode ? (
							<>
								<Button
									variant="outlined"
									size="small"
									startIcon={<AddIcon />}
									data-test-id="add-row-button"
									onClick={() => {
										setEditRow(null);
										setAddRowOpen(true);
									}}
									sx={{
										backgroundColor: (theme) => theme.palette.background.paper,
									}}
								>
									Add Row
								</Button>
								<Button
									variant="outlined"
									size="small"
									color="error"
									startIcon={<DeleteOutlineIcon />}
									onClick={() => setConfirm("delete")}
									disabled={discardMutation.isPending}
									data-test-id="delete-draft-button"
									sx={{
										backgroundColor: (theme) => theme.palette.background.paper,
									}}
								>
									Delete Draft
								</Button>
								<Button
									variant="contained"
									color="success"
									size="small"
									startIcon={<PublishIcon />}
									onClick={() => setConfirmVersionOpen(true)}
									disabled={confirmMutation.isPending || isSubmitting}
									data-test-id="confirm-draft-button"
								>
									Confirm
								</Button>
							</>
						) : isScheduled ? (
							backToDraftButton
						) : hasDraft ? (
							<Button
								variant="outlined"
								size="small"
								startIcon={<VisibilityOutlinedIcon />}
								onClick={() => setSelectedVersionId(draftVersionId)}
								data-test-id="see-draft-button"
								sx={{
									backgroundColor: (theme) => theme.palette.background.paper,
								}}
							>
								See Draft
							</Button>
						) : (
							<>
								{isLatestPublished && backToDraftButton}
								{isLatestPublished && canEditPublished && (
									<Tooltip
										title={
											hasDraft
												? "There is already a draft. Edit or delete it before editing the published revision."
												: ""
										}
									>
										<span>
											<Button
												variant="outlined"
												size="small"
												startIcon={<EditOutlinedIcon />}
												data-test-id="edit-published-button"
												onClick={() => setIsEditingPublished(true)}
												disabled={hasDraft}
												sx={{
													backgroundColor: (theme) =>
														theme.palette.background.paper,
												}}
											>
												Edit Published
											</Button>
										</span>
									</Tooltip>
								)}
								<Tooltip
									title={
										hasPendingVersion
											? "There is already a scheduled version"
											: ""
									}
								>
									<span data-test-id="new-version-button-wrapper">
										<Button
											variant="contained"
											size="small"
											startIcon={<EditIcon />}
											data-test-id="new-version-button"
											onClick={() => setNewVersionOpen(true)}
											disabled={
												createVersionMutation.isPending || hasPendingVersion
											}
										>
											New Draft
										</Button>
									</span>
								</Tooltip>
							</>
						)}
					</Box>
				</Box>

				<Box sx={{ display: "flex" }}>
					<TextFieldSearchFilter
						value={filterValue}
						onChange={(val) => {
							setFilterValue(val);
							gridApi?.setGridOption("quickFilterText", val);
						}}
					/>
				</Box>

				<Box sx={{ flex: 1, minHeight: 0 }}>
					<Paper elevation={2} style={{ height: "100%", width: "100%" }}>
						<AgGridReact<TestsBySection>
							loading={testsLoading}
							rowData={tests}
							columnDefs={columnDefs}
							defaultColDef={defaultColDef}
							context={
								{
									onToggle: (row, field) => saveRow(applyToggle(row, field)),
									onEdit: handleEditRow,
									onDelete: handleDelete,
									isEditMode,
									isSubmitLoading: isSubmitting,
								} satisfies CheckboxCellContext
							}
							rowHeight={34}
							headerHeight={34}
							animateRows
							singleClickEdit
							stopEditingWhenCellsLoseFocus
							getContextMenuItems={getContextMenuItems}
							onGridReady={(params) => setGridApi(params.api)}
							getRowId={(params) =>
								`${params.data.SectionId}-${params.data.GradeId ?? ""}`
							}
							loadingOverlayComponent={CenteredLazyLoading}
							onCellValueChanged={onCellValueChanged}
						/>
					</Paper>
				</Box>

				{selectedVersion?.Comments && (
					<Box>
						<Typography variant="body2" color="text.disabled">
							Notes:
						</Typography>
						<Typography
							variant="body2"
							color="text.secondary"
							data-test-id="version-comments"
							sx={{ whiteSpace: "pre-line" }}
						>
							{selectedVersion.Comments}
						</Typography>
					</Box>
				)}
			</Box>

			<NewVersionModal
				open={newVersionOpen}
				isSubmitting={createVersionMutation.isPending}
				onClose={() => setNewVersionOpen(false)}
				onSubmit={({ comments }) =>
					createVersionMutation.mutate({
						fromVersionId: versionId,
						comments,
					})
				}
			/>

			<ConfirmVersionModal
				open={confirmVersionOpen}
				maxRev={maxRev}
				lastEffectiveDate={lastEffectiveDate}
				lastUsedDate={allVersions?.[0]?.LastTestRunAt ?? null}
				lastUsedLabel="the last day mechanical test results were received"
				allowPastDates
				isSubmitting={confirmMutation.isPending}
				onClose={() => setConfirmVersionOpen(false)}
				onSubmit={({ rev, effectiveDate }) => {
					if (versionId == null) return;
					confirmMutation.mutate({ versionId, rev, effectiveDate });
				}}
			/>

			<AddRowModal
				open={addRowOpen}
				selectedRow={editRow}
				existingRows={tests ?? []}
				correctingVersionId={isCorrecting ? versionId : null}
				isSubmitting={upsertMutation.isPending}
				onClose={() => {
					setAddRowOpen(false);
					setEditRow(null);
				}}
				onSubmit={handleSaveRow}
			/>

			<ConfirmationDialogRaw
				open={confirm !== null}
				title={
					confirm === "changeToDraft"
						? "Change Revision to Draft"
						: "Delete Draft"
				}
				message={
					confirm !== "changeToDraft"
						? "Delete and discard this draft? Confirmed revisions are unaffected."
						: isScheduled
							? "Change scheduled revision to Draft? Its saved tests will be preserved. It will no longer become effective automatically."
							: `Change Rev ${selectedVersion?.Rev} to Draft? Its saved tests will be preserved. The previous revision goes back into force until this one is confirmed again.`
				}
				buttons={{
					no: "Cancel",
					yes: confirm === "changeToDraft" ? "Back to Draft" : "Delete Draft",
				}}
				onClose={handleConfirmClose}
			/>

			<ConfirmationDialogRaw
				open={rowToDelete !== null}
				title="Delete Row"
				message={`Delete the tests of ${rowToDelete?.SectionName ?? ""} (${rowToDelete?.GradeName ?? "All grades"}) from this ${isCorrecting ? `published revision (Rev ${selectedVersion?.Rev})` : "draft"}?`}
				buttons={{ no: "Cancel", yes: "Delete" }}
				onClose={handleDeleteRowClose}
			/>

			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
